import { useEffect, useMemo, useState } from "react";
import { MoreHorizontal, X } from "lucide-react";
import { useEditorContext } from "@/lib/editorContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CREATE_WORKSPACE_VALUE = "__create_workspace";

const formatRelativeTime = (timestamp: number) => {
  if (!timestamp) return "Auto-saving";
  const diffMs = Date.now() - timestamp;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 5) return "Auto-saved just now";
  if (seconds < 60) return `Auto-saved ${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Auto-saved ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Auto-saved ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `Auto-saved ${days}d ago`;
};

export function EditorPanelWorkspaceControls() {
  const {
    workspaces,
    activeWorkspaceId,
    workspaceName,
    selectWorkspace,
    createWorkspace,
    renameWorkspace,
    deleteWorkspace,
    lastModified,
  } = useEditorContext();
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState(workspaceName);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  const canDelete = workspaces.length > 1;

  useEffect(() => {
    setRenameValue(workspaceName);
    setDeleteConfirm("");
  }, [workspaceName, manageModalOpen]);

  const autoSaveLabel = useMemo(() => formatRelativeTime(lastModified), [lastModified]);

  const handleWorkspaceChange = (nextValue: string) => {
    if (nextValue === CREATE_WORKSPACE_VALUE) {
      createWorkspace();
      return;
    }
    selectWorkspace(nextValue);
  };

  const handleRename = () => {
    const trimmed = renameValue.trim();
    if (!trimmed || !activeWorkspaceId) return;
    renameWorkspace(activeWorkspaceId, trimmed);
    setManageModalOpen(false);
  };

  const handleDelete = () => {
    if (!canDelete || !activeWorkspaceId || deleteConfirm !== workspaceName) return;
    const removed = deleteWorkspace(activeWorkspaceId);
    if (removed) {
      setManageModalOpen(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={activeWorkspaceId ?? undefined} onValueChange={handleWorkspaceChange}>
              <SelectTrigger className="min-w-48 cursor-pointer" aria-label="Workspace selector">
                <SelectValue placeholder="Select workspace">{workspaceName}</SelectValue>
              </SelectTrigger>
              <SelectContent align="start" className="min-w-56">
                {workspaces.map(workspace => (
                  <SelectItem key={workspace.id} value={workspace.id} className="cursor-pointer">
                    <div className="flex flex-col text-left">
                      <span className="font-medium leading-tight">{workspace.name}</span>
                      <span className="text-[11px] text-muted-foreground">{formatRelativeTime(workspace.lastModified)}</span>
                    </div>
                  </SelectItem>
                ))}
                <SelectSeparator />
                <SelectItem value={CREATE_WORKSPACE_VALUE} className="cursor-pointer">
                  <span className="font-medium text-primary">+ Create workspace</span>
                </SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              aria-label="Manage workspace"
              className="cursor-pointer border-border/70 hover:border-border hover:bg-accent/60 hover:text-accent-foreground"
              onClick={() => setManageModalOpen(true)}
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </div>
          <span className="rounded-full border border-border/60 px-3 py-1 text-xs text-muted-foreground">
            {autoSaveLabel}
          </span>
        </div>
        <p className="text-xs text-muted-foreground my-2.5">
          Organize your timelines per workspace. Numbered lists only; use Tab/Shift+Tab to change hierarchy. Dates are auto-detected.
        </p>
      </div>
      {manageModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm" onClick={() => setManageModalOpen(false)}>
          <div
            className="w-full max-w-md rounded-2xl border border-border bg-card/95 p-6 shadow-2xl"
            onClick={event => event.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-card-foreground">Manage workspace</h3>
              <button
                type="button"
                aria-label="Close workspace manager"
                className="rounded-full border border-transparent p-1 text-muted-foreground transition hover:border-border hover:text-card-foreground cursor-pointer"
                onClick={() => setManageModalOpen(false)}
              >
                <X className="size-4" />
              </button>
            </div>
            <div className="mt-4 space-y-5">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rename</p>
                <div className="flex gap-2">
                  <Input value={renameValue} onChange={event => setRenameValue(event.target.value)} />
                  <Button
                    type="button"
                    onClick={handleRename}
                    disabled={!renameValue.trim()}
                    className="cursor-pointer transition hover:-translate-y-0.5"
                  >
                    Save
                  </Button>
                </div>
              </div>
              <div className="space-y-2 rounded-2xl border border-border/70 bg-destructive/5 p-4">
                <div>
                  <p className="text-sm font-semibold text-card-foreground">Delete workspace</p>
                  <p className="text-xs text-muted-foreground">
                    This cannot be undone. Type the workspace name to confirm.
                  </p>
                </div>
                <Input
                  placeholder={workspaceName}
                  value={deleteConfirm}
                  onChange={event => setDeleteConfirm(event.target.value)}
                />
                <Button
                  type="button"
                  variant="destructive"
                  className="w-full cursor-pointer transition hover:-translate-y-0.5"
                  disabled={!canDelete || deleteConfirm !== workspaceName}
                  onClick={handleDelete}
                >
                  Delete workspace
                </Button>
                {!canDelete && (
                  <p className="text-center text-xs text-muted-foreground">At least one workspace is required.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
